import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaDownload, FaFileInvoice, FaHome, FaSpinner, FaEye, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useTheme } from '../../context/SocketContext';

const PaymentSuccess = () => {
  const { currentThemeColors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!paymentId || !orderId) {
      toast.error('Invalid payment confirmation');
      navigate('/pricing');
      return;
    }

    const fetchInvoice = async () => {
      try {
        // Invoice is already generated in backend during payment verification
        // Just fetch the user data to get the invoice URL
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        
        // Get the latest invoice (first in array after sorting by date)
        const invoices = response.data.user.invoices || [];
        if (invoices.length > 0) {
          const latestInvoice = invoices[invoices.length - 1]; // Last invoice is the latest
          const fullInvoiceUrl = `${process.env.REACT_APP_API_BASE_URL}${latestInvoice.fileUrl}`;
          setInvoiceUrl(fullInvoiceUrl);
        } else {
          toast.warning('Invoice will be available shortly');
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        toast.error('Failed to load invoice');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoice();
  }, [paymentId, orderId, navigate]);

  const downloadInvoice = () => {
    if (!invoiceUrl) return;
    window.open(invoiceUrl, '_blank');
  };

  const previewInvoice = () => {
    if (!invoiceUrl) return;
    setShowPreview(true);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <div className="card p-8 max-w-md w-full text-center opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        {/* Success Icon */}
        <div 
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce"
          style={{ background: 'var(--primary-100)' }}
        >
          <FaCheckCircle className="w-10 h-10" style={{ color: 'var(--primary-500)' }} />
        </div>
        
        <h2 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">Payment Successful!</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Thank you for your purchase.</p>

        {/* Payment Details */}
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 mb-6 text-left space-y-2">
          <p className="text-sm text-dark-800 dark:text-gray-300">
            <span className="font-medium text-gray-500 dark:text-gray-400">Order ID:</span> {orderId}
          </p>
          <p className="text-sm text-dark-800 dark:text-gray-300">
            <span className="font-medium text-gray-500 dark:text-gray-400">Payment ID:</span> {paymentId}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isLoading ? (
            <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-500 text-white opacity-70 cursor-not-allowed" disabled>
              <FaSpinner className="w-4 h-4 animate-spin" /> Loading Invoice...
            </button>
          ) : (
            <>
              <button 
                onClick={previewInvoice}
                disabled={!invoiceUrl}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaEye className="w-4 h-4" /> Preview Invoice
              </button>
              <button 
                onClick={downloadInvoice}
                disabled={!invoiceUrl}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FaDownload className="w-4 h-4" /> Download Invoice
              </button>
            </>
          )}

          <button
            onClick={() => navigate('/Invoice')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white hover:scale-105 transition-all duration-300"
            style={{ 
              background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
              boxShadow: `0 10px 15px -3px var(--glow-color)`
            }}
          >
            <FaFileInvoice className="w-4 h-4" /> View All Invoices
          </button>

          <button
            onClick={() => navigate('/pricing')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
          >
            <FaHome className="w-4 h-4" /> Go to Dashboard
          </button>
        </div>
      </div>

      {/* Invoice Preview Modal */}
      {showPreview && invoiceUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowPreview(false)}>
          <div className="relative w-full max-w-5xl h-[90vh] bg-white dark:bg-dark-800 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700">
              <h3 className="text-lg font-semibold text-dark-800 dark:text-white">Invoice Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              >
                <FaTimes className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            {/* PDF Viewer */}
            <iframe
              src={invoiceUrl}
              className="w-full h-[calc(100%-60px)]"
              title="Invoice Preview"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSuccess;
